import React from 'react';
import { HeroImage } from '../components/HeroImage';
import { Handshake, Zap, Globe, Mail } from 'lucide-react';

export const Partnerships = () => {
  const partnerCategories = [
    {
      icon: <Zap size={40} />,
      title: 'GoHighLevel',
      description: 'We build on and integrate deeply with the GoHighLevel platform, providing enhanced frontends and automation capabilities.',
      color: 'accent'
    },
    {
      icon: <Globe size={40} />,
      title: 'AI Platforms',
      description: 'Integration with leading AI services for natural language processing, image generation, and intelligent automation.',
      color: 'accent'
    },
    {
      icon: <Handshake size={40} />,
      title: 'Automation Tools',
      description: 'Seamless connections with popular automation platforms, CRMs, and marketing tools to extend your workflows.',
      color: 'accent'
    }
  ];

  const idealPartners = [
    'Platform providers looking for enhanced integrations',
    'Agencies seeking white-label solutions',
    'SaaS companies wanting to extend their ecosystem',
    'Technology partners for strategic collaborations',
    'Resellers and distributors in relevant markets'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <HeroImage
        title="PARTNERSHIPS"
        subtitle="We collaborate with platforms, agencies, and technology providers to deliver better solutions together."
      />

      <div className="container mx-auto px-5 md:px-10">

        {/* Who We Work With - White Background */}
        <section className="py-20 md:py-32 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="heading-2 text-center mb-16">WHO WE WORK WITH</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {partnerCategories.map((category) => (
                <div
                  key={category.title}
                  className="bg-black border border-gray-800 rounded-xl p-6 hover:border-[#f43cde] transition-all hover:shadow-[0_0_30px_rgba(244,60,222,0.3)]"
                >
                  <div className="text-[#f43cde] mb-4">
                    {category.icon}
                  </div>
                  <h3 className="heading-5 mb-3">{category.title}</h3>
                  <p className="body-medium text-on-dark">{category.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What We Offer Partners - Black Background */}
        <section className="py-20 md:py-32 bg-black">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 text-center mb-16">WHAT WE OFFER PARTNERS</h2>
            
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="bg-gradient-to-br from-[#00d9ff] via-[#0066ff] to-[#f43cde] text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="heading-5 mb-2">Deep Integration Capabilities</h3>
                  <p className="body-medium text-on-dark">
                    Technical expertise to build robust, scalable integrations with your platform or service.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-gradient-to-br from-[#00d9ff] via-[#0066ff] to-[#f43cde] text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="heading-5 mb-2">Modern Frontend Solutions</h3>
                  <p className="body-medium text-on-dark">
                    AI-powered interfaces that enhance user experience and increase platform engagement.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-gradient-to-br from-[#00d9ff] via-[#0066ff] to-[#f43cde] text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="heading-5 mb-2">Ecosystem Expansion</h3>
                  <p className="body-medium text-on-dark">
                    Access to our product suite and user base for mutual growth opportunities.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-gradient-to-br from-[#00d9ff] via-[#0066ff] to-[#f43cde] text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                  4
                </div>
                <div>
                  <h3 className="heading-5 mb-2">Technical Support & Documentation</h3>
                  <p className="body-medium text-on-dark">
                    Comprehensive resources and dedicated support for integration and deployment.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Ideal Partners - White Background */}
        <section className="py-20 md:py-32 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 mb-8">WHO SHOULD CONTACT US</h2>
            <div className="bg-black border border-gray-800 rounded-xl p-8 md:p-12">
              <p className="body-large text-on-dark mb-6">
                We're actively seeking partnerships with:
              </p>
              <ul className="space-y-3">
                {idealPartners.map((partner, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-[#f43cde] text-xl mt-1">•</span>
                    <p className="body-medium text-on-dark">{partner}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Integration Showcase - Black Background */}
        <section className="py-20 md:py-32 bg-black">
          <div className="container mx-auto px-5 md:px-10">
            <h2 className="heading-2 text-center mb-8">INTEGRATION CAPABILITIES</h2>
            <p className="body-large text-center mb-12 max-w-3xl mx-auto text-on-dark text-muted-dark">
              Our products are built with integration-first architecture, making it easy to connect with your existing tools and workflows.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <div className="bg-white border border-gray-200 rounded-xl p-6 text-center hover:border-[#f43cde] transition-all">
                <p className="caption text-[#f43cde]">REST APIS</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-6 text-center hover:border-[#f43cde] transition-all">
                <p className="caption text-[#f43cde]">WEBHOOKS</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-6 text-center hover:border-[#f43cde] transition-all">
                <p className="caption text-[#f43cde]">OAUTH 2.0</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-6 text-center hover:border-[#f43cde] transition-all">
                <p className="caption text-[#f43cde]">REAL-TIME SYNC</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA - White Background */}
        <section className="py-20 md:py-32 bg-white">
          <div className="max-w-3xl mx-auto bg-black border border-gray-800 rounded-xl p-8 md:p-12 text-center">
            <Mail size={48} className="text-[#f43cde] mx-auto mb-6" />
            <h2 className="heading-2 mb-4">LET'S TALK</h2>
            <p className="body-large text-on-dark mb-8">
              Interested in partnering with us? We'd love to explore how we can work together.
            </p>
            <div className="space-y-4">
              <a
                href="mailto:contact@optiflo.app"
                className="btn-primary inline-block"
              >
                Email Us: contact@optiflo.app
              </a>
              <p className="body-small text-on-dark text-muted-dark">
                For partnership inquiries, please include:
                <br />
                Your company name, partnership type of interest, and how you envision working together.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};